import { useState, useRef } from "react";
import axios from "axios";
import useSWR, { RevalidatorOptions } from "swr";
import { GateState, QueueConfig, QueueStatus } from "@features/queue/types";
import { getPollInterval } from "@utils/queue";
import { handle429s } from "@utils/fetchers";

const CONFIG_REFRESH_MS = 60000;

export default function useQueue(queueSlug?: string) {
  const [queueState, setQueueState] = useState<GateState>("loading");
  const [queueStatus, setQueueStatus] = useState<QueueStatus>({
    state: "waiting",
  });
  const [opensAt, setOpensAt] = useState<number | null>(null);
  const admittedRef = useRef(false);

  useSWR<QueueConfig>(
    queueSlug && queueState !== "admitted"
      ? `/api/queue/config?slug=${encodeURIComponent(queueSlug)}`
      : null,
    async (url) => {
      const res = await axios.get(url);
      return res.data as QueueConfig;
    },
    {
      refreshInterval: CONFIG_REFRESH_MS,
      refreshWhenHidden: true,
      onErrorRetry: (error, key, config, revalidate, { retryCount }) => {
        if (admittedRef.current) return;
        if (error?.response?.status === 429) {
          return handle429s(error, key, config, revalidate, {
            retryCount,
          } as Required<RevalidatorOptions>);
        } else if (retryCount >= 3) {
          // fail open
          setQueueState("open");
        } else {
          const delayMs = Math.min(Math.max(retryCount, 1), 5) * 1000;
          setTimeout(() => revalidate({ retryCount }), delayMs);
        }
      },
      onSuccess: (cfg) => {
        if (!cfg.enabled) {
          setQueueState("open");
          return;
        }
        if (
          admittedRef.current ||
          queueStatus.state === "active" ||
          queueState === "admitted" ||
          cfg.admitted
        ) {
          setQueueState("admitted");
          admittedRef.current = true;
          return;
        }
        const opens = cfg.opensAt ? Date.parse(cfg.opensAt) : 0;
        setOpensAt(Number.isNaN(opens) ? 0 : opens);
        if (!opens || Date.now() >= opens) {
          setQueueState("queue");
        } else {
          setQueueState("browse");
        }
      },
    }
  );

  useSWR<QueueStatus>(
    queueState === "queue" && queueSlug
      ? `/api/queue/status?slug=${encodeURIComponent(queueSlug)}`
      : null,
    async (url) => {
      const res = await axios.get(url);
      return res.data as QueueStatus;
    },
    {
      refreshInterval:
        queueState === "queue"
          ? getPollInterval(
              queueStatus.ahead ?? 0,
              queueStatus.estimatedWaitSeconds
            )
          : 0,
      refreshWhenHidden: true,
      // rely on swr's default exponential backoff to handle 429s
      onError: () => {
        setQueueStatus({ state: "error" });
      },
      onSuccess: (s) => {
        setQueueStatus(s);
        if (s.state === "active" || s.state === "disabled") {
          admittedRef.current = true;
          setQueueState("admitted");
        }
      },
    }
  );

  return {
    queueState,
    queueStatus,
    setQueueState,
    opensAt,
    admittedRef,
  };
}
